/**
 * #765 C22 i18n framework V1 — MOTOR react-i18next.
 *
 * F1 (2026-06-26): se adopta react-i18next como motor real, DEBAJO de la API
 * pública existente (`useI18n` / `useTranslation` en I18nProvider.tsx y el
 * `t()` del feature ERP). Decisión de stack firmada por Randhy 2026-06-26
 * (react-i18next + i18next-parser frontend · nestjs-i18n api · RLS app-level).
 *
 * Por qué esto NO rompe nada (FAIL GRACEFULLY):
 *   - Los message-dicts existentes (`messages/*.ts`) entran como namespace
 *     `common` SIN renombrar keys (keySeparator:false → keys planas tipo
 *     "common.save" se buscan literales, no como nested common→save).
 *   - El JSON del ERP (`features/erp/i18n/locales/es-DO/erp.json`) entra como
 *     namespace `erp`, también keys planas.
 *   - Interpolación configurada a llave-simple `{var}` para respetar el
 *     formato ya escrito en erp.json (i18next por defecto usa `{{var}}`).
 *   - languageDetector reusa la MISMA key de localStorage que el provider
 *     casero ("inbomarket_locale") → continuidad del idioma guardado.
 *
 * Mejora gratis: i18next da fallback cross-locale (es-MX/es-CO incompletos →
 * caen a es-DO), cosa que el provider casero NO hacía.
 *
 * Extracción de keys + locales erp en-US/es-MX/es-CO = F2 (i18next-parser).
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type SupportedLocale } from './config';
import { MESSAGES } from './messages';
import erpEsDO from '@/features/erp/i18n/locales/es-DO/erp.json';

type Bundle = Record<string, Record<string, string>>;

const resources = SUPPORTED_LOCALES.reduce<Record<SupportedLocale, Bundle>>(
  (acc, locale) => {
    acc[locale] = { common: MESSAGES[locale] };
    return acc;
  },
  {} as Record<SupportedLocale, Bundle>,
);

// El namespace `erp` arranca solo en es-DO (locale canónico). Los demás locales
// caen a es-DO vía fallbackLng hasta que F2 extraiga sus traducciones.
resources[DEFAULT_LOCALE].erp = erpEsDO as Record<string, string>;

// #765 F2 — catálogos por feature auto-cargados via glob.
// Cada archivo `src/shared/i18n/locales/<locale>/<namespace>.json` se registra
// solo (namespace = nombre del archivo). i18next-parser mantiene estos JSON
// sincronizados con los t('ns.key', { ns }) del código. Sin matches → {} (ok).
const featureCatalogs = import.meta.glob<Record<string, string>>(
  './locales/*/*.json',
  { eager: true, import: 'default' },
);
const KNOWN_LOCALES = new Set<string>(SUPPORTED_LOCALES);
for (const [filePath, dict] of Object.entries(featureCatalogs)) {
  // filePath: './locales/es-DO/agente.json'
  const m = filePath.match(/\.\/locales\/([^/]+)\/([^/]+)\.json$/);
  if (!m) continue;
  const [, locale, ns] = m;
  if (!KNOWN_LOCALES.has(locale)) continue;
  // Merge: lo ya presente (p.ej. `common` de MESSAGES, o `erp`) GANA sobre el
  // catálogo del parser, para no pisar traducciones existentes con vacíos.
  const existing = resources[locale as SupportedLocale][ns];
  resources[locale as SupportedLocale][ns] = existing
    ? { ...dict, ...existing }
    : dict;
}

// Lista de namespaces = unión de todos los presentes en cualquier locale.
const ALL_NAMESPACES = Array.from(
  new Set(
    SUPPORTED_LOCALES.flatMap((l) => Object.keys(resources[l] ?? {})),
  ),
);

if (!i18n.isInitialized) {
  void i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [...SUPPORTED_LOCALES],
      load: 'currentOnly',
      ns: ALL_NAMESPACES,
      defaultNS: 'common',
      fallbackNS: 'common',
      // Keys planas con puntos ("common.save") — NO tratar el punto como
      // separador de nodos, y los dos puntos tampoco como separador de ns
      // (el ns se pasa siempre por options.ns).
      keySeparator: false,
      nsSeparator: false,
      interpolation: {
        prefix: '{',
        suffix: '}',
        escapeValue: false, // React escapa; nuestros valores son texto plano.
      },
      detection: {
        order: ['localStorage', 'navigator'],
        lookupLocalStorage: 'inbomarket_locale',
        caches: ['localStorage'],
      },
      react: {
        useSuspense: false, // recursos inline → init síncrono, sin Suspense.
      },
      returnNull: false,
      // Los locales no-canónicos (es-CO/es-MX/en-US) tienen stubs vacíos del
      // i18next-parser. Sin esto, una key "" se considera "encontrada" y NO cae
      // al fallbackLng (es-DO) → labels vacíos (dashboard de agencia se veía
      // hueco). returnEmptyString:false hace que "" caiga al fallback/default.
      returnEmptyString: false,
    });
}

export default i18n;
